import { InputAdornment, TextField } from '@ds/coolshop';
import { TextFieldProps } from '@src/assets/types';
import { parseLocaleNumber } from '@src/utils/generic';
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useController, UseControllerProps, useFormContext, Validate } from 'react-hook-form';
import { useIntl } from 'react-intl';

// Grouped display never invents precision: 8 decimals is the same ceiling the XLSX export uses.
const MAX_DISPLAYED_DECIMALS = 8;

function TextFieldElement({
    control,
    id,
    name,
    label,
    type = 'text',
    defaultValue = '',
    measureFieldProps,
    readOnly = false,
    disabled = false,
    fullWidth = true,
    multiLine = false,
    setIsoCode,
    required = false,
    helperText,
    readOnlyMessage,
    onKeyDown,
    validationRule = () => true,
    onChange = () => {},
    LanguageComponent,
    showAsPercentage = false,
    disableThousandsSeparator = false,
    InputProps,
    rules,
}: Readonly<
    TextFieldProps & {
        InputProps?: { endAdornment?: ReactNode };
        showAsPercentage?: boolean;
        disableThousandsSeparator?: boolean;
    }
>) {
    const intl = useIntl();

    const isNumber = type === 'number';

    // Numeric fields accept any locale format the user pastes ("4.500,00", "€ 1 234,50"), so an
    // unreadable entry is kept in the form as typed and rejected here: that blocks the save
    // instead of silently storing a mangled value.
    const validateNumber: Validate<any, any> = value => {
        // a multiValue field carries an array: an empty one is an unfilled field, not a bad
        // number, and String([]) would otherwise reach the parser as ''
        const entries = (Array.isArray(value) ? value : [value]).filter(
            entry => entry !== undefined && entry !== null && entry !== '',
        );
        if (!entries.length) {
            return true;
        }
        if (entries.every(entry => parseLocaleNumber(String(entry), intl.locale) !== null)) {
            return true;
        }
        return intl.formatMessage({
            id: 'field.invalidNumber',
            defaultMessage: 'Invalid number',
        });
    };

    const resolvedRules: UseControllerProps['rules'] = isNumber
        ? {
              ...rules,
              validate:
                  typeof rules?.validate === 'function'
                      ? { rule: rules.validate, localeNumber: validateNumber }
                      : { ...rules?.validate, localeNumber: validateNumber },
          }
        : rules;

    const { field, fieldState } = useController({
        name,
        control,
        defaultValue,
        rules: resolvedRules,
    });

    const { watch, setValue, getValues } = useFormContext();

    const { unitFieldName, convertToUnit } = measureFieldProps || {};

    const { isoCode, fieldName, addIsoCode } = setIsoCode || {};

    const watchUnitField = watch(unitFieldName as string);

    // A percentage is stored as a fraction and shown as a percentage
    const toScaledValue = (val: string) =>
        showAsPercentage && val !== '' && !Number.isNaN(parseFloat(val))
            ? String(parseFloat(val) * 100)
            : val;

    // Stored values are canonical ('.' decimal, no grouping), so they are read with Number and
    // never with the locale parser: under `it` the latter would take the '.' of "0.125" for a
    // thousands mark. The locale parser is for what the user types, nothing else.
    //
    // Digits are grouped like any other amount, unless the content type opts out: a numeric field
    // also carries years and ordinals, where "2.026" would be plain wrong.
    const toDisplayValue = (val: string | number) => {
        // stored contents can hold actual numbers, not just strings: coerce before any
        // string handling, or .trim() below throws on mount
        const scaled = toScaledValue(String(val));
        if (!isNumber || scaled.trim() === '') {
            return scaled;
        }
        const parsed = Number(scaled);
        return Number.isFinite(parsed)
            ? intl.formatNumber(parsed, {
                  useGrouping: !disableThousandsSeparator,
                  maximumFractionDigits: MAX_DISPLAYED_DECIMALS,
              })
            : scaled;
    };

    // The value as it is stored: the entered text read against the user's locale, percentages
    // scaled back down. An unreadable entry is kept as typed so the validation can reject it.
    const toStoredValue = (val: string) => {
        if (!isNumber) {
            return showAsPercentage && val !== '' && !Number.isNaN(parseFloat(val))
                ? String(parseFloat(val) / 100)
                : val;
        }
        // what the user typed is the one thing read against the locale
        if (val.trim() === '') {
            return '';
        }
        // "12," is a decimal the user stopped typing halfway: it means 12
        const parsed = parseLocaleNumber(val.replace(/[.,\s]+$/, ''), intl.locale);
        if (parsed === null) {
            return val;
        }
        return String(showAsPercentage ? parsed / 100 : parsed);
    };

    const [inputValue, setInputValue] = useState(toDisplayValue(field.value ?? ''));
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastFormValueRef = useRef<string>(field.value ?? '');

    const commitValue = (newValue: string) => {
        const storedValue = toStoredValue(newValue);
        // an unchanged value costs a form-wide render for nothing
        if (storedValue === lastFormValueRef.current) {
            return storedValue;
        }
        lastFormValueRef.current = storedValue;
        field.onChange(storedValue);
        onChange(storedValue);
        return storedValue;
    };

    // "12," or "-" is a number being typed, not a broken one: an entry counts as still in
    // progress while appending a digit would make it readable. Committing those mid-keystroke
    // pushed a half-typed value into the form and flashed the field red between characters.
    const isInProgress = (val: string) => parseLocaleNumber(`${val}0`, intl.locale) !== null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;

        // Update local state immediately for lag-free typing
        setInputValue(newValue);

        // Clear existing timeout
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Debounce form value update
        debounceTimeoutRef.current = setTimeout(() => {
            debounceTimeoutRef.current = null;
            if (isNumber && newValue.trim() !== '' && isInProgress(newValue)) {
                // still being typed: the blur commits it
                return;
            }
            commitValue(newValue);
        }, 300);
    };

    const handleBlur = () => {
        if (!isNumber) {
            return;
        }
        // Commit what is in the box, then show it in the user's locale. commitValue is a
        // no-op when nothing changed, so a mere focus/blur never dirties the form.
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
            debounceTimeoutRef.current = null;
        }
        const storedValue = commitValue(inputValue);
        setInputValue(toDisplayValue(storedValue));
    };

    useEffect(() => {
        // required conversion function for the measure field
        if (watchUnitField && convertToUnit && unitFieldName) {
            const convertedValue = convertToUnit(field.value, watchUnitField).toString();
            // Clear any pending debounce to prevent overwriting the conversion
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
            setInputValue(toDisplayValue(convertedValue));
            lastFormValueRef.current = convertedValue;
            field.onChange(convertedValue);
        }
    }, [watchUnitField]);

    useEffect(() => {
        if (isoCode && fieldName && addIsoCode) {
            if (!getValues(fieldName)) {
                setValue(fieldName, isoCode);
            }
        }
    }, [isoCode]);

    // Sync local value with field value when it changes externally (e.g., form reset)
    // Only sync if the change is external (not from our debounced update)
    useEffect(() => {
        const currentFieldValue = field.value ?? '';
        // Only sync if field value changed externally (different from what we last set)
        if (currentFieldValue !== lastFormValueRef.current && currentFieldValue !== inputValue) {
            // Clear any pending debounce to prevent overwriting the external change
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }
            setInputValue(toDisplayValue(currentFieldValue));
            lastFormValueRef.current = currentFieldValue;
        }
    }, [field.value, inputValue]);

    // Cleanup debounce timeout on unmount
    useEffect(
        () => () => {
            clearTimeout(debounceTimeoutRef.current ?? undefined);
        },
        [],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isNumber && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
            e.preventDefault();
            return;
        }
        onKeyDown?.(e);
    };

    const percentageAdornment = showAsPercentage ? (
        <InputAdornment position="end">%</InputAdornment>
    ) : null;

    let resolvedEndAdornment: ReactNode;
    if (LanguageComponent) {
        resolvedEndAdornment = <InputAdornment position="end">{LanguageComponent}</InputAdornment>;
    } else if (InputProps?.endAdornment || percentageAdornment) {
        resolvedEndAdornment = (
            <>
                {InputProps?.endAdornment}
                {percentageAdornment}
            </>
        );
    }

    // errors raised by the form validation rules carry their own message
    const validationError = fieldState.error?.message;
    const isInvalid = !!fieldState.error || !validationRule(field.value);
    const getResolvedHelperText = () => {
        if (validationError) {
            return validationError;
        }
        if (isInvalid) {
            return helperText ?? 'invalid entry';
        }
        if (readOnly && readOnlyMessage) {
            return readOnlyMessage;
        }
        return '';
    };

    return (
        <TextField
            error={isInvalid}
            helperText={getResolvedHelperText()}
            id={id}
            label={label}
            fullWidth={fullWidth}
            // numeric fields are plain text inputs: type="number" makes the browser strip the
            // separators of a pasted "4.500,00" before the value ever reaches us
            type={isNumber ? 'text' : type}
            inputRef={(input: HTMLInputElement | null) => {
                if (isNumber) {
                    input?.setAttribute('inputmode', 'decimal');
                }
            }}
            onChange={handleChange} // send value to hook form
            onBlur={handleBlur}
            value={inputValue} // input value
            disabled={disabled || readOnly || field.disabled}
            required={required}
            multiline={multiLine}
            onKeyDown={handleKeyDown}
            rows={8}
            minWidth="md"
            endAdornment={resolvedEndAdornment}
        />
    );
}

export default TextFieldElement;
